import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { isOwner, isStaff, useAuth } from "../auth.js";
import { CONSENT_CHANGED, consentKey, readConsent, readTestingExclusion, safePage, saveConsent, saveTestingExclusion, TESTING_EXCLUSION_CHANGED, TESTING_EXCLUSION_KEY, usageAnalytics, type AnalyticsAccount, type Consent } from "./usage.js";
import "./usage.css";

function sameConsent(previous: Consent | null, next: Consent | null) {
  return previous?.choice === next?.choice && previous?.expiresAt === next?.expiresAt;
}

function useAnalyticsAccount(): AnalyticsAccount | null {
  const { me, loading, bootstrapError } = useAuth();
  if (!me?.user?.id || !Array.isArray(me.roles) || loading || bootstrapError) return null;
  const audience = isStaff(me) ? "staff" : me.roles.includes("parent") ? "family" : null;
  // A student-only or unrecognised role never inherits a parent's permission.
  return audience ? { id: me.user.id, audience, ...(isOwner(me) ? { excluded: true } : {}) } : null;
}

function useTestingExclusion(enforce = false) {
  const [excluded, setExcluded] = useState(readTestingExclusion);
  useLayoutEffect(() => {
    let previous: boolean | null = null;
    const update = () => {
      const next = readTestingExclusion();
      if (enforce && next && previous !== next) usageAnalytics.stop(true);
      previous = next;
      setExcluded(current => current === next ? current : next);
    };
    const storage = (event: StorageEvent) => { if (event.key === null || event.key === TESTING_EXCLUSION_KEY) update(); };
    window.addEventListener(TESTING_EXCLUSION_CHANGED, update);
    window.addEventListener("storage", storage);
    window.addEventListener("focus", update);
    update();
    return () => {
      window.removeEventListener(TESTING_EXCLUSION_CHANGED, update);
      window.removeEventListener("storage", storage);
      window.removeEventListener("focus", update);
    };
  }, [enforce]);
  return excluded;
}

function useConsent(accountId: string | null, enforce = false) {
  const [snapshot, setSnapshot] = useState(() => ({ accountId, consent: readConsent(accountId) }));
  // An account switch must not render the previous account's choice while its
  // effect is waiting to run, even if both accounts use the same family area.
  const consent = snapshot.accountId === accountId ? snapshot.consent : readConsent(accountId);
  useEffect(() => {
    let timer: number;
    let previous: Consent | null = null;
    let initialized = false;
    const update = () => {
      window.clearTimeout(timer);
      const next = readConsent(accountId);
      // Only the application boundary enforces changes. The notice/settings
      // observers must not repeat cookie cleanup or render unchanged choices.
      if (enforce && accountId && next?.choice !== "accepted" && (!initialized || !sameConsent(previous, next))) usageAnalytics.stop(true);
      previous = next;
      initialized = true;
      setSnapshot(current => current.accountId === accountId && sameConsent(current.consent, next) ? current : { accountId, consent: next });
      // Stop at expiry even in a long-lived PWA tab. Short rescheduling also
      // handles clock changes and avoids the browser's maximum timer delay.
      timer = window.setTimeout(update, next ? Math.max(1, Math.min(60000, next.expiresAt - Date.now() + 1)) : 60000);
    };
    const storage = (event: StorageEvent) => { if (event.key === null || (accountId && event.key === consentKey(accountId))) update(); };
    window.addEventListener(CONSENT_CHANGED, update);
    window.addEventListener("storage", storage);
    window.addEventListener("focus", update);
    update();
    return () => {
      window.removeEventListener(CONSENT_CHANGED, update);
      window.removeEventListener("storage", storage);
      window.removeEventListener("focus", update);
      window.clearTimeout(timer);
    };
  }, [accountId, enforce]);
  return consent;
}

export function UsageConsentChoices({ accountId, settings = false }: { accountId: string; settings?: boolean }) {
  const consent = useConsent(accountId);
  const testingExcluded = useTestingExclusion();
  const [error, setError] = useState(false);
  function choose(choice: Consent["choice"]) {
    // Stop synchronously, including when storage becomes unavailable.
    if (choice === "declined") usageAnalytics.stop(true);
    setError(!saveConsent(accountId, choice));
  }
  if (!settings && (testingExcluded || (consent && !error))) return null;
  return <section className={`usage-consent ${settings ? "panel account-card" : "usage-consent-notice"}`} aria-label="Optional usage analytics">
    <h2>{settings ? "Usage analytics" : "Help improve WMF Hub"}</h2>
    <p>With your permission, we use Google Analytics cookies to understand which Hub features are used and how often people return. The Hub works with either choice.</p>
    <details>
      <summary>What is collected?</summary>
      <p>Google receives general page names, feature activity, whether you use the staff or family area, browser and device information, and approximate location. We do not send names, email addresses, student IDs, lesson content or private links.</p>
      <p>Your choice applies to this account on this browser for up to 180 days. Tutor and parent accounts make their own choice; owner and student-only accounts are not measured. Your account ID is not sent to Google.</p>
      <p>Cookies recognise this browser for up to 180 days. You can change your choice in Your account → Usage analytics. Turning analytics off stops future collection and removes the Hub’s analytics cookies from this browser.</p>
      <p><a href="https://wirralmusicfactory.com/privacy-policy/" target="_blank" rel="noopener noreferrer">WMF privacy policy</a> · <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google privacy policy</a></p>
    </details>
    {settings && <p role="status">{testingExcluded
      ? `Analytics is excluded on this browser while testing. Your saved choice for this account is ${consent?.choice === "accepted" ? "on" : "off"}.`
      : `Analytics is ${consent?.choice === "accepted" ? "on" : "off"} for this account on this browser.`}</p>}
    <div className="usage-consent-actions">
      <button type="button" className="quiet" onClick={() => choose("declined")}>{settings ? "Turn analytics off" : "Keep analytics off"}</button>
      <button type="button" className="quiet" disabled={testingExcluded} onClick={() => choose("accepted")}>Allow analytics</button>
    </div>
    {error && <p role="alert">Your browser could not save this choice. Analytics remains off. Please allow this site to save preferences and try again.</p>}
  </section>;
}

export function UsageAnalytics() {
  const account = useAnalyticsAccount();
  const { pathname } = useLocation();
  const consent = useConsent(account?.id ?? null, true);
  const testingExcluded = useTestingExclusion(true);
  useLayoutEffect(() => {
    usageAnalytics.sync(pathname, account);
  }, [pathname, account?.id, account?.audience, account?.excluded, consent?.choice, consent?.expiresAt, testingExcluded]);
  useEffect(() => () => usageAnalytics.stop(), []);
  if (!usageAnalytics.enabled || !account || account.excluded || testingExcluded || !safePage(pathname)) return null;
  return <UsageConsentChoices key={account.id} accountId={account.id} />;
}

export function UsageAnalyticsSettings() {
  const account = useAnalyticsAccount();
  if (!usageAnalytics.enabled || !account) return null;
  return <>
    {account.excluded ? <section className="usage-consent panel account-card" aria-label="Usage analytics exclusion">
      <h2>Usage analytics</h2>
      <p role="status">Your owner account is excluded from Google Analytics on every browser, including when analytics was previously allowed.</p>
    </section> : <UsageConsentChoices key={account.id} accountId={account.id} settings />}
    {account.audience === "staff" && <UsageTestingExclusion />}
  </>;
}

function UsageTestingExclusion() {
  const excluded = useTestingExclusion();
  const [error, setError] = useState(false);
  return <section className="usage-consent panel account-card" aria-label="Browser testing exclusion">
    <h2>Testing on this browser</h2>
    <p>Exclude this browser before testing with other accounts. The exclusion applies to every account in this browser until you include it again.</p>
    <p role="status">{excluded ? "This browser is excluded from usage analytics." : "This browser follows each account’s analytics choice."}</p>
    <div className="usage-consent-actions">
      <button type="button" className="quiet" onClick={() => setError(!saveTestingExclusion(!excluded))}>
        {excluded ? "Include this browser again" : "Exclude this browser while testing"}
      </button>
    </div>
    {error && <p role="alert">Your browser could not save this choice. Analytics is excluded in this tab. Please allow this site to save preferences and try again.</p>}
  </section>;
}
